import { useEffect, useState } from "react";
import axios from "axios";
import { Download, Loader2, X, FileText, Trash2 } from "lucide-react";

const ACCENT = "#4fb3a4";
const API = `${process.env.REACT_APP_BACKEND_URL}/api/securest`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });
const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (d) => { try { return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); } catch { return d; } };

export const downloadPdf = (b64, filename) => {
  const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

export const SecurestSavedQuotes = ({ onClose }) => {
  const [quotes, setQuotes] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    axios.get(`${API}/quotes`, { headers: authHeaders() }).then((r) => setQuotes(r.data)).catch(() => setQuotes([]));
  }, []);

  const download = async (ref) => {
    setBusy(ref);
    try {
      const r = await axios.get(`${API}/quotes/${ref}/pdf`, { headers: authHeaders() });
      downloadPdf(r.data.pdf_base64, r.data.filename);
    } catch {
      alert("Could not download that quote. Please try again.");
    }
    setBusy("");
  };

  const remove = async (ref) => {
    if (!window.confirm(`Delete quote ${ref}? It will be removed from your saved quotes.`)) return;
    setBusy(`del-${ref}`);
    try {
      await axios.delete(`${API}/quotes/${ref}`, { headers: authHeaders() });
      setQuotes((qs) => qs.filter((q) => q.quote_ref !== ref));
    } catch {
      alert("Could not delete that quote. Please try again.");
    }
    setBusy("");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ backgroundColor: "rgba(0,0,0,0.65)" }} data-testid="securest-saved-quotes">
      <div className="rounded-xl border w-full max-w-3xl max-h-[85vh] flex flex-col" style={{ borderColor: "#28323e", backgroundColor: "#0f151c" }}>
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: "#1c2530" }}>
          <div className="flex items-center gap-2 text-white font-semibold"><FileText className="h-5 w-5" style={{ color: ACCENT }} />Saved quotes</div>
          <button data-testid="securest-saved-close" onClick={onClose} className="text-slate-400 hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        <div className="overflow-auto">
          {quotes === null ? (
            <div className="p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" style={{ color: ACCENT }} /></div>
          ) : quotes.length === 0 ? (
            <div className="p-8 text-sm text-center" style={{ color: "#64748b" }} data-testid="securest-saved-empty">No saved quotes yet. Quotes are saved when you click Download &amp; Email.</div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-xs uppercase tracking-wider" style={{ color: "#64748b" }}>
                <th className="px-4 py-3 text-left">Quote</th><th className="px-4 py-3 text-left">Customer / site</th>
                <th className="px-4 py-3 text-left">System</th><th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3" />
              </tr></thead>
              <tbody>
                {quotes.map((q) => (
                  <tr key={q.quote_ref} className="border-t" style={{ borderColor: "#1c2530" }} data-testid={`securest-saved-row-${q.quote_ref}`}>
                    <td className="px-4 py-3"><div className="text-white font-medium">{q.quote_ref}</div><div className="text-xs" style={{ color: "#64748b" }}>{fmt(q.created_at)}{q.submitted_by ? ` · ${q.submitted_by}` : ""}</div></td>
                    <td className="px-4 py-3" style={{ color: "#cbd5e1" }}>{q.customer_reference || "—"}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: "#94a3b8" }}>{q.doors} door(s) · {q.cameras} camera(s)</td>
                    <td className="px-4 py-3 text-right" style={{ color: ACCENT }}>{gbp(q.total)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button data-testid={`securest-saved-download-${q.quote_ref}`} onClick={() => download(q.quote_ref)} disabled={busy === q.quote_ref}
                        className="text-xs px-3 py-1.5 rounded font-semibold text-white inline-flex items-center gap-1 disabled:opacity-60" style={{ backgroundColor: ACCENT }}>
                        {busy === q.quote_ref ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} PDF
                      </button>
                      <button data-testid={`securest-saved-delete-${q.quote_ref}`} onClick={() => remove(q.quote_ref)} disabled={busy === `del-${q.quote_ref}`}
                        title="Delete quote" className="ml-2 text-xs px-2 py-1.5 rounded border inline-flex items-center text-slate-400 hover:text-red-400 hover:border-red-400 transition-colors disabled:opacity-60" style={{ borderColor: "#28323e" }}>
                        {busy === `del-${q.quote_ref}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};
